/**
 * Subscription Hooks
 */
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { subscriptionService } from '../services/subscriptionService';
import { queryTags } from '../queryTags';
import { getQueryClient } from '../queryClient';
import type {
  GetSubscriptionsParams,
  UpdateSubscriptionPayload,
  GetAllTransactionsParams,
  UpdateSubscriptionSettingsPayload,
} from '../types/subscription.types';
import type { PaginationParams, SearchParams } from '../types';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

/**
 * Get users who purchased a subscription hook
 */
export function useGetUserPurchases(
  id: string,
  params?: PaginationParams & SearchParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.subscription, 'user-purchase', id, params?.page || 1, params?.limit || 10, params?.search || ''],
    queryFn: () => subscriptionService.getUserPurchases(id, params),
    enabled: !!id,
    ...options,
  });
}

/**
 * Get subscription settings hook
 */
export function useGetSubscriptionSettings(
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.subscription, 'settings'],
    queryFn: () => subscriptionService.getSettings(),
    ...options,
  });
}

/**
 * Update subscription settings hook
 */
export function useUpdateSubscriptionSettings(
  options?: UseMutationOptions<any, any, UpdateSubscriptionSettingsPayload, any>
) {
  return useApiMutation<any, any, UpdateSubscriptionSettingsPayload, any>({
    mutationFn: (data: UpdateSubscriptionSettingsPayload) => subscriptionService.updateSettings(data),
    onSuccess: (data, variables, context, mutation) => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.subscription, 'settings'] });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    onError: options?.onError,
  });
}

/**
 * Get subscriptions list hook
 */
export function useGetSubscriptions(
  params?: GetSubscriptionsParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [
      queryTags.subscription,
      params?.page || 1,
      params?.limit || 10,
      params?.search || '',
      params?.type,
    ],
    queryFn: () => subscriptionService.getSubscriptions(params),
    ...options,
  });
}

/**
 * Get single subscription by ID hook
 */
export function useGetSubscription(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.subscription, id],
    queryFn: () => subscriptionService.getSubscription(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Update subscription hook
 */
export function useUpdateSubscription(
  options?: UseMutationOptions<any, any, { id: string; data: UpdateSubscriptionPayload }, any>
) {
  return useApiMutation<any, any, { id: string; data: UpdateSubscriptionPayload }, any>({
    mutationFn: ((variables: { id: string; data: UpdateSubscriptionPayload }) =>
      subscriptionService.updateSubscription(variables.id, variables.data)) as any,
    onSuccess: (data, variables, context, mutation) => {
      // Always invalidate queries to refresh the list
      getQueryClient().invalidateQueries({ queryKey: [queryTags.subscription] });
      // Call custom onSuccess if provided
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    onError: options?.onError,
  });
}

/**
 * Get user subscription details hook
 */
export function useGetUserSubscription(
  userId: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.subscription, 'user-subscription', userId],
    queryFn: () => subscriptionService.getUserSubscription(userId),
    enabled: !!userId,
    ...options,
  });
}

/**
 * Get all transactions hook
 */
export function useGetAllTransactions(
  params?: GetAllTransactionsParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.transaction, 'list', params?.page || 1, params?.limit || 10, params?.search || ''],
    queryFn: () => subscriptionService.getAllTransactions(params),
    ...options,
  });
}

